import React from 'react';
import { Ingredient, Macros } from '@llm-macro-tracker/shared';

interface MacroDisplayProps {
  macros: Macros;
  goals?: {
    dailyCalorieGoal?: number;
    macroGoals?: {
      protein: number;
      carbs: number;
      fat: number;
    };
  };
  ingredients?: Ingredient[];
  showFullInfo?: boolean;
}

export const MacroDisplay: React.FC<MacroDisplayProps> = ({
  macros,
  goals,
  ingredients,
  showFullInfo = true,
}) => {
  const calculateMacroGoal = (macroType: 'protein' | 'carbs' | 'fat', macroPercentage: number) => {
    if (goals?.dailyCalorieGoal && goals.macroGoals) {
      const caloriesFromMacro = (goals.dailyCalorieGoal * macroPercentage) / 100;
      const caloriesPerGram = macroType === 'fat' ? 9 : 4;
      return Math.round(caloriesFromMacro / caloriesPerGram);
    }
    return undefined;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 relative z-0">
      <h3 className="text-lg font-semibold text-gray-700 mb-3">Nutritional Information</h3>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <MacroItem
          icon="🔥"
          name="Calories"
          value={macros.calories}
          goal={showFullInfo ? goals?.dailyCalorieGoal : undefined}
          unit=""
        />
        <MacroItem
          icon="🥩"
          name="Protein"
          value={macros.protein}
          goal={
            showFullInfo
              ? calculateMacroGoal('protein', goals?.macroGoals?.protein ?? 0)
              : undefined
          }
          unit="g"
        />
        <MacroItem
          icon="🍞"
          name="Carbs"
          value={macros.carbs}
          goal={
            showFullInfo ? calculateMacroGoal('carbs', goals?.macroGoals?.carbs ?? 0) : undefined
          }
          unit="g"
        />
        <MacroItem
          icon="🥑"
          name="Fat"
          value={macros.fat}
          goal={showFullInfo ? calculateMacroGoal('fat', goals?.macroGoals?.fat ?? 0) : undefined}
          unit="g"
        />
      </div>
      {showFullInfo && ingredients && ingredients.length > 0 && (
        <div>
          <h4 className="font-semibold text-gray-700 mb-2">Ingredients:</h4>
          <ul className="space-y-2">
            {ingredients.map((ingredient, index) => (
              <li key={index} className="bg-gray-50 p-3 rounded-md">
                <div className="font-medium text-gray-800 mb-1">{ingredient.name}</div>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                  <span>{ingredient.calories} cal</span>
                  <span>{ingredient.protein}g protein</span>
                  <span>{ingredient.carbs}g carbs</span>
                  <span>{ingredient.fat}g fat</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

interface MacroItemProps {
  icon: string;
  name: string;
  value: number;
  goal?: number;
  unit: string;
}

const MacroItem: React.FC<MacroItemProps> = ({ icon, name, value, goal, unit }) => {
  const percentage = goal ? (value / goal) * 100 : 0;
  const isExceeded = value > (goal ?? Infinity);

  return (
    <div className="bg-gray-50 rounded-lg p-3 flex items-center relative overflow-hidden">
      <div
        className={`absolute inset-0 opacity-50 ${isExceeded ? 'bg-red-200' : 'bg-green-200'}`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      />
      <div className="text-2xl mr-3 z-10">{icon}</div>
      <div className="z-10">
        <div className="font-semibold text-gray-700">{name}</div>
        <div className="text-lg font-bold text-gray-800">
          {value.toFixed(1).replace(/\.0$/, '')}
          {unit}
        </div>
        {goal !== undefined && (
          <div className="text-sm text-gray-600">
            Goal: {goal}
            {unit} ({percentage.toFixed(0)}%)
          </div>
        )}
      </div>
    </div>
  );
};

export default MacroDisplay;
